import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowUpRight,
  ShieldCheck,
  Wind,
  Layers,
  FileCheck2,
} from "lucide-react";
import { MaskLines, Reveal, Label, CTASection } from "../components/Shared";
import Ribbon from "../components/Ribbon";
import { DIFFERENTIATORS, AUDIENCES, IMAGES } from "../data/site";

const ICONS = { ShieldCheck, Wind, Layers, FileCheck2 };

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const overlayY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  return (
    <section
      ref={ref}
      className="relative min-h-[100svh] overflow-hidden bg-navy"
      data-testid="home-hero"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={IMAGES.cleanroomMask}
          alt="MD Labs certified cleanroom"
          className="h-full w-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/60 via-navy/70 to-navy" />
      </motion.div>

      {/* technical grid lines */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.12]">
        <div className="mx-auto grid h-full max-w-[1400px] grid-cols-4 px-6 lg:grid-cols-12 lg:px-12">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="border-l border-white/40 last:border-r" />
          ))}
        </div>
      </div>

      <motion.div
        style={{ y: overlayY }}
        className="relative mx-auto flex min-h-[100svh] max-w-[1400px] flex-col justify-center px-6 pt-32 pb-40 lg:px-12"
      >
        <Reveal>
          <Label light>Westlake Village, California · FDA-Regulated Facility</Label>
        </Reveal>

        <h1 className="mt-8 font-heading text-[13vw] font-extrabold leading-[0.94] tracking-tighter text-white sm:text-7xl lg:text-[6.4rem]">
          <MaskLines
            once
            delay={0.15}
            lines={["FDA-Cleared.", "ISO-Certified."]}
          />
          <span className="mt-2 block text-clinical">
            <MaskLines once delay={0.4} lines={["Private Label"]} />
          </span>
          <MaskLines once delay={0.52} lines={["Suppository Manufacturing."]} />
        </h1>

        <div className="mt-10 grid gap-8 lg:grid-cols-12">
          <Reveal delay={0.7} className="lg:col-span-6">
            <p className="max-w-xl text-lg leading-relaxed text-slate-300 lg:text-xl">
              A medical device manufacturer specializing in vaginal suppositories,
              handcrafted in a certified ISO 7 cleanroom — not mass-produced.
            </p>
          </Reveal>
          <Reveal delay={0.85} className="flex items-end gap-4 lg:col-span-6 lg:justify-end">
            <Link
              to="/contact"
              data-testid="hero-primary-cta"
              className="group inline-flex items-center gap-3 bg-clinical px-8 py-4 text-base font-semibold text-white transition-colors duration-200 hover:bg-white hover:text-navy"
            >
              Start a Conversation
              <ArrowUpRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <Link
              to="/capabilities"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center gap-2 border border-white/30 px-8 py-4 text-base font-semibold text-white transition-colors duration-200 hover:border-white"
            >
              Our Capabilities
            </Link>
          </Reveal>
        </div>
      </motion.div>

      <div className="absolute inset-x-0 bottom-0">
        <Ribbon dark />
      </div>
    </section>
  );
};

const Overview = () => (
  <section className="bg-[#FAFAFA] py-24 lg:py-32">
    <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
      <div className="grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <Reveal>
            <Label>What We Do</Label>
          </Reveal>
        </div>
        <div className="lg:col-span-8">
          <Reveal delay={0.1}>
            <p className="font-heading text-3xl font-bold leading-[1.25] tracking-tight text-navy sm:text-4xl lg:text-[2.75rem]">
              MD Labs manufactures vaginal suppositories in a certified,
              ISO-classified cleanroom — holding a 510(k) clearance from the FDA
              and operating under strict quality and regulatory standards.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-slate-600">
              We partner with wellness brands, physicians and healthcare companies
              who need a trusted private label manufacturer — one that can take a
              product from concept to a finished, fully documented and labeled
              result.
            </p>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

const Differentiators = () => (
  <section className="bg-white py-24 lg:py-32" data-testid="differentiators">
    <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
      <div className="mb-16 flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <Reveal>
          <Label>Core Differentiators</Label>
          <h2 className="mt-6 font-heading text-4xl font-extrabold tracking-tight text-navy sm:text-5xl">
            Credentials that set us apart.
          </h2>
        </Reveal>
      </div>

      <div className="grid grid-cols-1 border-t border-l border-slate-200 sm:grid-cols-2 lg:grid-cols-4">
        {DIFFERENTIATORS.map((d, i) => {
          const Icon = ICONS[d.icon];
          return (
            <Reveal
              key={d.no}
              delay={i * 0.08}
              className="group border-b border-r border-slate-200"
            >
              <div className="flex h-full flex-col p-8 transition-colors duration-300 hover:bg-navy lg:p-10">
                <div className="flex items-center justify-between">
                  <Icon className="h-8 w-8 text-clinical transition-transform duration-300 group-hover:scale-110" />
                  <span className="font-display text-sm font-bold text-slate-300 transition-colors duration-300 group-hover:text-slate-500">
                    {d.no}
                  </span>
                </div>
                <h3 className="mt-10 font-heading text-xl font-bold text-navy transition-colors duration-300 group-hover:text-white">
                  {d.title}
                </h3>
                <p className="mt-4 text-[15px] leading-relaxed text-slate-500 transition-colors duration-300 group-hover:text-slate-300">
                  {d.body}
                </p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const WhoWeWorkWith = () => (
  <section className="bg-[#FAFAFA] py-24 lg:py-32" data-testid="who-we-work-with">
    <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
      <div className="mb-16 grid gap-8 lg:grid-cols-12">
        <Reveal className="lg:col-span-7">
          <Label>Who We Work With</Label>
          <h2 className="mt-6 font-heading text-4xl font-extrabold tracking-tight text-navy sm:text-5xl">
            Built for serious B2B partners.
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-5 lg:pt-16">
          <p className="text-lg leading-relaxed text-slate-600">
            Whether you're launching a brand or extending an existing catalog, we
            provide the compliant, documented foundation your product needs.
          </p>
        </Reveal>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {AUDIENCES.map((a, i) => (
          <Reveal key={a.title} delay={i * 0.1}>
            <div className="group overflow-hidden bg-white">
              <div className="relative aspect-[4/5] overflow-hidden">
                <img
                  src={a.img}
                  alt={a.title}
                  className="h-full w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
                />
                <div className="absolute inset-0 bg-navy/20 transition-opacity duration-500 group-hover:opacity-0" />
              </div>
              <div className="border-x border-b border-slate-200 p-8">
                <h3 className="font-heading text-2xl font-bold text-navy">
                  {a.title}
                </h3>
                <p className="mt-3 text-[15px] leading-relaxed text-slate-500">
                  {a.body}
                </p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const MANIFESTO = [
  {
    no: "01",
    title: "Handcrafted, not mass-produced.",
    body: "Every batch is made with intention inside a controlled cleanroom — precision over scale, quality over shortcuts.",
  },
  {
    no: "02",
    title: "Certified and documented.",
    body: "FDA 510(k) clearance and ISO 7 cleanroom certification mean everything we produce is backed by verifiable documentation.",
  },
  {
    no: "03",
    title: "Your brand, our facility.",
    body: "We manufacture; you own the brand. Custom formulations built from your ingredients, delivered finished and labeled.",
  },
];

const Manifesto = () => (
  <section className="bg-white py-24 lg:py-32" data-testid="manifesto">
    <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
      <Reveal>
        <Label>Why MD Labs</Label>
        <h2 className="mt-6 max-w-3xl font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-navy sm:text-5xl">
          A manufacturing partner obsessed with trust.
        </h2>
      </Reveal>

      <div className="mt-16 divide-y divide-slate-200 border-y border-slate-200">
        {MANIFESTO.map((m, i) => (
          <Reveal key={m.no} delay={i * 0.08}>
            <div className="group relative grid gap-6 py-12 lg:grid-cols-12 lg:py-16">
              <div className="lg:col-span-4">
                <span className="font-display text-7xl font-extrabold text-slate-100 transition-colors duration-300 group-hover:text-clinical/20 lg:text-8xl">
                  {m.no}
                </span>
              </div>
              <h3 className="lg:col-span-4 font-heading text-2xl font-bold text-navy sm:text-3xl">
                {m.title}
              </h3>
              <p className="lg:col-span-4 self-center text-lg leading-relaxed text-slate-500">
                {m.body}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <Overview />
      <Differentiators />
      <WhoWeWorkWith />
      <Manifesto />
      <CTASection />
    </>
  );
}
